import { Device } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useDevicesTranslation } from '../hooks';
import { addOrUpdateAllDevicesCache } from '../queries/devices-cache';

/**
 * The device name and team/member assignment chosen on the activation details
 * page. A team is required (the API needs one to set the name, and a device
 * needs a team for alerts); the member is optional.
 */
export type ActivationDetails = {
    name: string;
    teamId: string;
    companyUserId: string | null;
};

/** The alert features chosen on the activation alerts page. */
export type ActivationAlerts = {
    fall: boolean;
    tilt: boolean;
};

// Default config applied when enabling a feature during activation. Matches the
// documented API defaults and the DEFAULT_* constants in the alert modals.
const ACTIVATION_FALL_SENSITIVITY = 4;
const ACTIVATION_TILT_ANGLE = 45;
const ACTIVATION_TILT_TIME_THRESHOLD = 180;

type SaveDetailsVariables = {
    deviceId: string;
    details: ActivationDetails;
};

type SaveAlertsVariables = {
    deviceId: string;
    alerts: ActivationAlerts;
};

type UseConfigureActivatedDeviceActionData = {
    saveDetails: (
        deviceId: string,
        details: ActivationDetails
    ) => Promise<Device>;
    /** Resolves with the device as each feature left it, in order. */
    saveAlerts: (
        deviceId: string,
        alerts: ActivationAlerts
    ) => Promise<Device[]>;
    loading: boolean;
    error: string;
};

/**
 * The two saves the activation wizard makes once a device has been claimed:
 * `saveDetails` names and assigns it, `saveAlerts` turns the chosen features on
 * with their default settings.
 *
 * They share a hook because they are two pages of one wizard with one error
 * message and one spinner between them, and because `saveAlerts` is itself a
 * sequence — fall then tilt, one at a time, so each response reflects the
 * previous change.
 *
 * No `isAllowed`/`reason`: the wizard is only reachable from the devices page,
 * which has already decided whether this viewer may add a device.
 */
export function useConfigureActivatedDeviceAction(): UseConfigureActivatedDeviceActionData {
    const { t } = useDevicesTranslation();
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. It covers both saves: every error
    // `PATCH /v3/devices/{deviceId}` and the two config routes declare gets a
    // branch, bar `COMPANY_NOT_FOUND`, which this namespace has nothing of its
    // own to say about and so falls through to the generic message.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'INVALID_TEAM')) {
                return t('shared.errors.team_not_found');
            }
            if (isApiError(error, 'INVALID_USER')) {
                return t('shared.errors.member_not_in_team');
            }
            if (isApiError(error, 'DEVICE_NOT_FOUND')) {
                return t('shared.errors.device_not_found');
            }
            if (isApiError(error, 'DEVICE_NOT_SETUP')) {
                return t('shared.errors.device_not_setup');
            }
            if (isApiError(error, 'NOT_SUBSCRIBED')) {
                return t('shared.errors.not_subscribed');
            }
            if (isApiError(error, 'NO_MORE_LICENSES')) {
                return t('shared.errors.no_more_licenses');
            }
            if (isApiError(error, 'NO_PERMISSION')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                // The server names the offending field; it is more use than
                // anything generic we could write.
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const detailsMutation = useMutation({
        mutationFn: (variables: SaveDetailsVariables) =>
            openApiRef.current.devices
                .updateDevice(variables.deviceId, {
                    name: variables.details.name,
                    teamId: variables.details.teamId,
                    companyUserId: variables.details.companyUserId
                })
                .then((response) => response.data.data.device),
        onSuccess: (device) => {
            addOrUpdateAllDevicesCache(queryClient, device);
        },
        // No success toast: the wizard moves to its next page, which says more
        // than a toast would.
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    const alertsMutation = useMutation({
        mutationFn: async (variables: SaveAlertsVariables) => {
            const { deviceId, alerts } = variables;
            const devicesApi = openApiRef.current.devices;
            const devices: Device[] = [];

            // One at a time, so each response reflects the previous change.
            if (alerts.fall) {
                const response = await devicesApi.updateDeviceConfigFallAlert(
                    deviceId,
                    {
                        enabled: true,
                        sensitivity: ACTIVATION_FALL_SENSITIVITY
                    }
                );
                devices.push(response.data.data.device);
            }
            if (alerts.tilt) {
                const response = await devicesApi.updateDeviceConfigTiltAlert(
                    deviceId,
                    {
                        enabled: true,
                        angle: ACTIVATION_TILT_ANGLE,
                        timeThreshold: ACTIVATION_TILT_TIME_THRESHOLD
                    }
                );
                devices.push(response.data.data.device);
            }

            return devices;
        },
        onSuccess: (devices) => {
            devices.forEach((device) => {
                addOrUpdateAllDevicesCache(queryClient, device);
            });
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    const error = detailsMutation.error ?? alertsMutation.error;

    return {
        saveDetails: (deviceId, details) =>
            detailsMutation.mutateAsync({ deviceId, details }),
        saveAlerts: (deviceId, alerts) =>
            alertsMutation.mutateAsync({ deviceId, alerts }),
        loading: detailsMutation.isPending || alertsMutation.isPending,
        error: error ? getErrorMessage(error) : ''
    };
}
